<script setup lang="ts">
import { trustStats } from "~/data/trust";

const { t } = useI18n();
const config = useRuntimeConfig();
const { trackEvent } = useAnalytics();

const whatsappDigits = computed(() =>
  (config.public.whatsappNumber || "+995505556117").replace(/[^0-9]/g, "")
);
const whatsappHref = computed(() => `https://wa.me/${whatsappDigits.value}`);
const telHref = computed(
  () => `tel:${config.public.whatsappNumber || "+995505556117"}`
);
const emailHref = computed(
  () => `mailto:${config.public.contactEmail || "info@georgiadream.ge"}`
);
const telegramHref = computed(
  () => config.public.telegramLink || "https://t.me/georgia_dream"
);
const instagramHref = computed(
  () => (config.public.instagramUrl as string) || ""
);
const facebookHref = computed(
  () => (config.public.faceBookUrl as string) || ""
);
const airbnbHref = trustStats.airbnbProfileUrl;

type Pill = {
  href: string;
  icon: string;
  label: string;
  iconClass?: string;
  external?: boolean;
  track?: string;
};

const pills = computed<Pill[]>(() => {
  const list: Pill[] = [
    { href: whatsappHref.value, icon: "mdi:whatsapp", label: t("contact_page.whatsapp_label"), iconClass: "text-green-600", external: true },
    { href: telegramHref.value, icon: "mdi:telegram", label: t("contact_page.telegram_label"), iconClass: "text-sky-500", external: true },
    { href: telHref.value, icon: "mdi:phone-outline", label: t("contact_page.phone_label") },
    { href: emailHref.value, icon: "mdi:email-outline", label: t("contact_page.email_label"), iconClass: "text-red-500" },
  ];
  if (instagramHref.value)
    list.push({ href: instagramHref.value, icon: "mdi:instagram", label: t("contact_page.instagram_label"), iconClass: "text-pink-500", external: true, track: "instagram_click" });
  if (facebookHref.value)
    list.push({ href: facebookHref.value, icon: "mdi:facebook", label: t("contact_page.facebook_label"), iconClass: "text-blue-600", external: true, track: "facebook_click" });
  list.push({ href: airbnbHref, icon: "simple-icons:airbnb", label: t("contact_page.airbnb_label"), iconClass: "text-rose-500", external: true, track: "airbnb_click" });
  return list;
});

function onPillClick(track?: string) {
  if (track) trackEvent(track, { placement: "contact_strip" });
}
</script>

<template>
  <section class="relative bg-white dark:bg-slate-900 pt-12 md:pt-16 pb-6 md:pb-8">
    <div class="container">
      <div class="max-w-5xl mx-auto" data-reveal>
        <div class="flex items-center gap-4 mb-6">
          <div class="h-px flex-1 bg-red-500/20" />
          <p class="text-xs uppercase tracking-[0.3em] text-red-500/90 font-semibold whitespace-nowrap">
            {{ t("contact_page.strip_eyebrow") }}
          </p>
          <div class="h-px flex-1 bg-red-500/20" />
        </div>

        <div class="flex flex-wrap justify-center gap-3">
          <a
            v-for="pill in pills"
            :key="pill.href"
            :href="pill.href"
            :target="pill.external ? '_blank' : undefined"
            :rel="pill.external ? 'noopener noreferrer' : undefined"
            class="group inline-flex items-center gap-2.5 h-11 px-5 rounded-full ring-1 ring-slate-200 dark:ring-slate-700 bg-white dark:bg-slate-900 text-sm font-medium text-slate-700 dark:text-slate-200 hover:ring-red-500 hover:bg-red-50 dark:hover:bg-red-500/5 hover:text-red-600 transition"
            @click="onPillClick(pill.track)"
          >
            <Icon :name="pill.icon" size="20px" :class="pill.iconClass" />
            <span>{{ pill.label }}</span>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>
